/**
 * OX 퀴즈 러너 — 퀘스트 스팟 태블릿 미션(ox_quiz).
 * 설계: docs/2026-06-19-quest-spot-mission-types-design.md §5.1·§5.2·§9.2 (D3 클라 즉시채점 / D10 서버 재채점)
 *
 * 동작:
 *   - mission_config.questions(1..10, 각 {qid,text,answer:"O"|"X"}) 중 하나를 진입 때 무작위 선택.
 *   - 선택된 한 문항에서 O/X 버튼 1회 선택 → 미션 완료(어린이 좌절방지: 참여=통과, U4).
 *   - 선택 시 onComplete({answers:[{qid,chosen}]}) 호출(답안은 항상 1개).
 *     · is_correct/correct 는 보내지 않는다 — 서버가 mission_version 스냅샷으로 재채점(D10).
 *     · 클라 즉시채점은 통과 게이팅·연출용일 뿐 저장 신뢰원이 아니다.
 *
 * 부모(QuestTagPage)가 tag 호출·결과 메시지를 담당. 본 컴포넌트는 미션 수행 + 결과 콜백만.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
// 완료 화면은 사진찍기와 **공용**이다(2026-08-14) — 그래서 이름에 미션명이 없다.
import MissionDoneScreen from './MissionDoneScreen';
import OxVerdictOverlay from './OxVerdictOverlay';
// 베이지 그라데이션 배경(.oxq-screen-beige)은 화면 ②와 **공유**한다 — 같은 값을 두 파일에
// 적으면 갈린다. 이 화면(③)이 그 토큰을 쓰므로 여기서도 import 한다.
import './OxQuizFlow.css';
import './OxQuizRunner.css';

/**
 * 마지막 문항의 정/오답 연출이 끝난 뒤 보여주는 "미션 대성공!" 화면 노출 시간(ms).
 * 이 시간이 지나면 onComplete → 부모가 tag 전송 후 QR 대기 화면(= OX 퀴즈 처음)으로 되돌린다.
 */
export const DONE_MS = 4000;

/**
 * 사용자가 누른 답(O/X)과 정오 조합에 따라 재생할 세트 공통 영상 URL.
 * 서버가 세트 설정을 각 OX `mission_config` 응답에 해석해 넣어준다.
 *
 * 🔴 **정오 판정은 표시용이다.** 저장·정산의 권위는 서버 재채점(D10)이고 `onComplete`
 *    페이로드는 바뀌지 않는다 — 어느 영상을 틀지 고르는 데만 쓴다.
 * 🔴 해당 영상이 없으면 `null` → 부모가 **건너뛰고 완료 화면으로** 간다(사용자 확정 R12).
 *    확장 스키마 이전 스팟은 영상이 아예 없다.
 */
export function resolveVerdictVideoUrl(config, chosen, isCorrect) {
  const answer = chosen === 'X' ? 'x' : chosen === 'O' ? 'o' : null;
  const commonUrl = answer
    ? config?.[`${answer}_${isCorrect ? 'correct' : 'incorrect'}_video_url`]
    : null;
  // 마이그170c 이전 세트는 선택 답과 무관한 correct/incorrect 2종만 갖는다.
  // 공통 4종이 없을 때만 그 계약으로 폴백해 운영 중인 레거시 세트를 보존한다.
  const legacyUrl = isCorrect ? config?.correct_video_url : config?.incorrect_video_url;
  const url = commonUrl || legacyUrl;
  return typeof url === 'string' && url.trim() !== '' ? url.trim() : null;
}

/**
 * 관리자가 입력한 문제 문구를 화면용 줄바꿈으로 정규화한다 (Figma 3715:70497/70507).
 *
 * 🔴 CRM 문항 입력은 antd `<Input>`(단일행)이라 **실제 개행 문자를 넣을 수 없다**
 *    (`crm/src/pages/Quests.jsx` MissionEditor). 관리자가 칠 수 있는 것은 역슬래시+n
 *    두 글자뿐이므로 그것을 줄바꿈으로 읽는다. 실제 개행(붙여넣기·API 직접 입력)도
 *    함께 받는다 — 한쪽만 지원하면 입력 경로에 따라 화면이 달라진다.
 *
 * 각 줄의 앞뒤 공백은 뗀다. `white-space: pre-line` 은 공백을 접어 주지만, 관리자가
 * `가나다 \n 라마바` 처럼 적었을 때 줄 끝 공백이 가운데정렬을 미세하게 밀 수 있다.
 */
export function normalizeQuestionText(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/\\r\\n|\\r|\\n/g, '\n') // 단일행 입력창에 적힌 리터럴 역슬래시+n (긴 것 먼저)
    .replace(/\r\n?/g, '\n') // 실제 개행(CRLF·CR)
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .trim();
}

/**
 * 문항 풀에서 한 문항을 균등 선택한다.
 *
 * `randomValue` 를 인자로 받는 순수 함수로 분리해 경계값과 선택 결과를 결정적으로 검증한다.
 * 운영에서는 기본값 Math.random()을 사용한다. 범위를 벗어난 값은 방어적으로 첫/마지막 문항에
 * 고정하며, 원본 배열은 변경하지 않는다.
 */
export function pickRandomQuestion(questions, randomValue = Math.random()) {
  if (!Array.isArray(questions) || questions.length === 0) return null;
  const numeric = Number(randomValue);
  const scaled = Number.isFinite(numeric) ? Math.floor(numeric * questions.length) : 0;
  const idx = Math.max(0, Math.min(questions.length - 1, scaled));
  return questions[idx];
}

/**
 * 문제 글자 자동 맞춤 (2026-08-18 사용자 결정) — 상자를 넘지 않게 글자를 줄인다.
 *
 * 🔴 왜 Figma 원본 86px 을 그대로 못 쓰나(실측): 실제 등록 문항이 상자(1580)를 넘는다.
 *    · '훔볼트 팽귄은 어른이 되면 목에 줄무늬가 생긴다.' → 86px GosanjaOTF 기준 **1830.5px**
 *    · '수달은 자신이 가장 아끼는 …' 첫 줄 → **1575px** (여유 5px — 기기가 조금만 달라도 넘침)
 *    넘치면 자동 줄바꿈이 일어나 **관리자가 의도한 줄 수가 어긋난다**(1줄→2줄, 2줄→3줄).
 *
 * 그래서 기본을 72px 로 낮추고(현재 전 문항이 약 3% 여유로 들어간다), 그래도 넘치는
 * 문항은 들어갈 때까지 더 줄인다. 지켜야 할 성질은 "글자가 작다" 가 아니라
 * **"화면 줄 수 = 관리자가 넣은 줄 수"** 다.
 *
 * 높이도 함께 본다 — 개행을 두 번 넣은 3줄 문항은 폭이 남아도 상자를 넘어 잘린다.
 */
export const OX_PROMPT_MAX_FONT = 72; // 기본이자 상한 (Figma 원본 86 → 사용자 결정으로 하향)
export const OX_PROMPT_MIN_FONT = 44; // 가독성 하한 — 이 밑으로는 줄이지 않는다
export const OX_PROMPT_BOX_WIDTH = 1580; // Figma 글자 상자 폭 (3756:36615)
export const OX_PROMPT_BOX_HEIGHT = 206; // Figma 글자 상자 높이
export const OX_PROMPT_LINE_HEIGHT = 1.2; // Figma 줄간격

/**
 * @param {string} text 정규화된 문제 문구(개행 포함)
 * @param {(line:string, fontSize:number)=>number} measureWidth 한 줄의 렌더 폭을 재는 함수
 * @returns {number} 적용할 글자 크기(Figma px)
 */
export function fitPromptFontSize(text, measureWidth, options = {}) {
  const {
    maxFont = OX_PROMPT_MAX_FONT,
    minFont = OX_PROMPT_MIN_FONT,
    boxWidth = OX_PROMPT_BOX_WIDTH,
    boxHeight = OX_PROMPT_BOX_HEIGHT,
    lineHeight = OX_PROMPT_LINE_HEIGHT,
  } = options;

  const lines = String(text ?? '').split('\n').filter((line) => line !== '');
  if (lines.length === 0) return maxFont;

  // 높이 제약은 측정 없이도 정해진다(줄 수 x 줄높이).
  const heightScale = boxHeight / (lines.length * maxFont * lineHeight);

  let widthScale = 1;
  if (typeof measureWidth === 'function') {
    const widest = lines.reduce((acc, line) => {
      const w = Number(measureWidth(line, maxFont));
      return Number.isFinite(w) && w > acc ? w : acc;
    }, 0);
    // 🔴 측정 실패(0·NaN)는 **기본 크기로 통과**시킨다 — 글자를 숨기거나 0px 로 만들지 않는다.
    if (widest > 0) widthScale = boxWidth / widest;
  }

  const scale = Math.min(1, widthScale, heightScale);
  if (scale >= 1) return maxFont;
  // 내림 — 반올림하면 1px 차이로 다시 넘칠 수 있다.
  return Math.max(minFont, Math.floor(maxFont * scale));
}

/**
 * 실제 렌더에 쓰이는 폰트로 한 줄의 폭을 재는 함수를 만든다.
 * 캔버스를 못 쓰는 환경(jsdom 등)에서는 `null` → 호출부가 기본 크기로 둔다.
 */
function createTextMeasurer(fontFamily) {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;
    return (line, fontSize) => {
      ctx.font = `${fontSize}px ${fontFamily}`;
      return ctx.measureText(line).width;
    };
  } catch (error) {
    return null; // 캔버스 미지원 — 기본 크기로 그린다
  }
}

/**
 * USB 1버튼 키보드 2개 → O/X 매핑 (2026-08-18 사용자 요청).
 *   F9 = O · F10 = X. 터치와 **같은 처리 경로**로 들어간다.
 *
 * 세 가지로 알아본다 — 기기마다 무엇을 주는지가 다르다.
 *   1) `key`     표준 브라우저. 'F9' / 'F10'
 *   2) `code`    값싼 USB 키보드·일부 안드로이드 웹뷰가 `key` 를 'Unidentified' 로 줄 때
 *   3) `keyCode` 레거시 경로(F9=120 · F10=121). deprecated 지만 웹뷰에서 아직 유효하다
 *
 * 🔴 수식키 조합(Alt+F9 등)은 사람이 의도한 시스템 단축키일 수 있어 제외한다.
 *    1버튼 키보드는 수식키를 함께 보내지 않으므로 현장 동작에 손해가 없다.
 * 🔴 `repeat`(누르고 있기)도 제외한다 — 한 번 누름 = 한 번 답이어야 한다.
 *    (연출 중 재입력은 러너의 answeringRef 가 또 막지만, 여기서 먼저 걸러 둔다.)
 */
const ANSWER_KEYS = { F9: 'O', F10: 'X' };
const ANSWER_KEY_CODES = { 120: 'O', 121: 'X' }; // F9 · F10 (레거시 keyCode)

export function resolveAnswerKey(event) {
  if (!event) return null;
  if (event.repeat) return null;
  if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return null;
  return ANSWER_KEYS[event.key]
    ?? ANSWER_KEYS[event.code]
    ?? ANSWER_KEY_CODES[event.keyCode]
    ?? null;
}

/**
 * 폼 입력에 포커스가 있으면 답으로 치지 않는다 — `useUsbQrReader` 와 같은 가드다.
 * 퀴즈 화면 자체에는 입력창이 없지만 관리자 바가 함께 떠 있을 수 있다.
 */
const isEditableTarget = (t) => !!t && (
  t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'
  || t.isContentEditable === true
);

/**
 * **안 푼** 문항 중 하나를 고른다 — 오답 재출제용 (2026-08-18 정책 변경).
 *
 * 🔴 이미 답한 문항을 다시 내면 안 되는 이유가 두 가지다.
 *    1) 아이가 같은 문제를 반복해 보며 갇힌다.
 *    2) 서버 `_regrade_ox` 가 **중복 qid 답안을 거부**한다(`quest_service.py` — "같은 OX 퀴즈
 *       문항에 중복 답변할 수 없습니다"). 즉 중복 출제는 미션 완료 자체를 실패시킨다.
 *
 * 후보가 없으면 `null` — 호출부는 이걸 "낼 문제가 떨어졌다" 로 읽고 미션완료로 보낸다(D-A).
 */
export function pickNextQuestion(questions, answeredQids, randomValue = Math.random()) {
  const answered = new Set(Array.isArray(answeredQids) ? answeredQids : []);
  const remaining = (Array.isArray(questions) ? questions : [])
    .filter((q) => q && !answered.has(q.qid));
  return pickRandomQuestion(remaining, randomValue);
}

/**
 * @param {object} props
 * @param {{questions:Array<{qid:number,text:string,answer:'O'|'X'}>}} props.config - mission_config
 * @param {(missionResult:{answers:Array<{qid:number,chosen:'O'|'X'}>})=>void} props.onComplete
 * @param {()=>void} [props.onCancel] - 중단(스팟 변경 등)
 * @param {string} [props.spotName] - 상단 제목에 넣을 퀴즈(스팟) 이름 — 서버가 내려준 값
 */
export default function OxQuizRunner({ config, onComplete, onCancel, spotName, onVideoAbort }) {
  // 방어: questions 가 비정상이면 빈 배열 — 부모가 "미션 설정 오류" 처리.
  const questions = useMemo(
    () => (Array.isArray(config?.questions) ? config.questions : []),
    [config]
  );

  // 리렌더로는 문항이 바뀌지 않는다. 바뀌는 경우는 **오답 재출제** 한 가지뿐이고, 그때만
  // setCurrent 로 교체한다(2026-08-18 정책). 다음 회원이 진입하면 컴포넌트가 새로 마운트되며
  // 처음부터 다시 뽑는다.
  const [current, setCurrent] = useState(() => pickRandomQuestion(questions));
  const [locked, setLocked] = useState(false); // 답 제출 후 버튼 비활성(UX 연출)
  // 이중탭 가드: 답 확정 시 즉시(동기) 재진입 차단. ref 라 리렌더 이전에도 유효
  // → tag 네트워크 왕복 동안 버튼이 살아있어도 onComplete 가 2번 불리지 않음(중복 tag·"이미 참여" 방지).
  const completedRef = useRef(false);
  // 정/오답 연출 중 재입력 차단 — state 는 리렌더 후에야 바뀌므로 같은 tick 의 두 번째 탭을 못 막는다.
  // 단일 문항이라도 이중탭으로 완료 타이머가 두 개 생기지 않게 ref 로 둔다.
  const answeringRef = useRef(false);
  // 연출 타이머 모음(정/오답 → 마무리 → onComplete). 언마운트 시 일괄 해제한다.
  const verdictTimerRef = useRef([]);

  // 'o' | 'x' | null — **정답인 패널의 위치**(내가 맞았는지가 아니다).
  // 이 값이 있는 동안 그 패널 위에 검은 체크가 깜박인다. 오답이어도 정답 쪽에 뜬다.
  // 마지막 문항까지 끝낸 뒤 보여주는 "미션 대성공!" 화면
  const [doneShown, setDoneShown] = useState(false);
  // 정답/오답 영상 오버레이 URL(없으면 이 단계를 건너뛴다 — R12)
  const [verdictVideoUrl, setVerdictVideoUrl] = useState(null);
  // 🔴 `onComplete` 페이로드를 단계 사이로 나른다. state 로 두면 리렌더 타이밍에 따라
  //    타이머 콜백이 옛 값을 볼 수 있다 — 답안은 **정확히 한 벌**이어야 한다.
  //    오답 재출제로 시도가 여러 번일 수 있어 **누적**한다(D-B: 시도 전부 전송).
  //    담긴 qid 목록이 곧 "이미 푼 문항" 이라 별도 목록을 두지 않는다 — 두 벌이면 갈린다.
  const answersRef = useRef([]);
  // 직전 답의 정오 — 정/오답 영상이 끝난 뒤 "완료로 갈지 재출제할지" 를 여기서 읽는다.
  // 영상 콜백은 handleAnswer 의 클로저 밖이라 값을 ref 로 넘겨야 한다.
  const lastCorrectRef = useRef(false);

  // 언마운트 시 연출 타이머 전부 해제 — 화면을 벗어난 뒤 onComplete 가 늦게 터지는 것을 막는다.
  useEffect(
    () => () => {
      verdictTimerRef.current.forEach(clearTimeout);
      verdictTimerRef.current = [];
    },
    []
  );

  const total = current ? 1 : 0;

  // 🔴 개정 디자인(3715:70497/70507)에는 **`Q.` 접두가 없다.** 관리자가 입력한 문구를 그대로 쓴다.
  //    ⚠️ 과거 관리자가 "Q." 로 시작하게 적어 둔 문항은 그대로 "Q.…" 로 보인다 — 데이터를
  //       손대지 않는다(표시 규칙 변경으로 저장값을 고치면 되돌릴 수 없다).
  const questionText = useMemo(() => normalizeQuestionText(current?.text), [current]);

  /**
   * 문제 글자 크기 — 문항이 바뀔 때마다 상자에 맞춰 다시 잰다.
   * 🔴 폰트(GosanjaOTF 10.2MB)가 늦게 뜨면 자폭이 달라진다 → `fonts.ready` 뒤 한 번 더 잰다.
   *    첫 측정을 건너뛰지는 않는다(폰트가 이미 캐시된 경우 즉시 맞춰야 깜빡임이 없다).
   */
  const promptRef = useRef(null);
  const [promptFont, setPromptFont] = useState(OX_PROMPT_MAX_FONT);

  useEffect(() => {
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      const family = promptRef.current
        ? getComputedStyle(promptRef.current).fontFamily
        : null;
      setPromptFont(fitPromptFontSize(questionText, family ? createTextMeasurer(family) : null));
    };
    apply();
    if (document.fonts?.ready?.then) document.fonts.ready.then(apply).catch(() => {});
    return () => { cancelled = true; };
  }, [questionText]);

  // 문항 첨부 이미지 — 서버가 `image_url` 로 해석해 내려준다(계약 C2).
  // 확장 스키마 이전 문항에는 키가 아예 없다 → `undefined` 하나로 판정된다(R8).
  const questionImageUrl = useMemo(() => {
    const url = current?.image_url;
    return typeof url === 'string' && url.trim() !== '' ? url.trim() : null;
  }, [current]);

  /**
   * 마무리 화면(Figma 3601:43294)을 `DONE_MS`(4초) 보여준 뒤 완료 통보 — 부모가 tag 를
   * 보내고 QR 대기 화면으로 되돌린다(사용자 요청 R10).
   *
   * 🔴 오버레이 경로와 건너뛰기 경로가 **같은 함수로** 들어온다. 두 군데에 타이머를
   *    복사해 두면 한쪽만 고쳐져 4초/5초가 갈린다.
   */
  const startDoneScreen = useCallback(() => {
    // 🔴 미션 종료 확정은 **여기 한 곳**이다. handleAnswer 에서 미리 세우면 오답 재출제
    //    경로에서도 잠겨 다음 문항을 못 푼다.
    completedRef.current = true;
    setVerdictVideoUrl(null);
    setDoneShown(true);
    verdictTimerRef.current.push(
      setTimeout(() => {
        const answers = answersRef.current;
        if (answers.length > 0) onComplete({ answers });
      }, DONE_MS)
    );
  }, [onComplete]);

  /**
   * 정/오답 연출이 끝난 뒤의 **단일 분기점** (2026-08-18 정책 변경).
   *   정답      → 미션완료화면 → QR 대기
   *   오답      → 안 푼 문항 재출제
   *   오답+소진 → 미션완료화면 (D-A: 참여=통과, 아이를 화면에 가두지 않는다)
   *
   * 🔴 영상이 있는 경로(오버레이 종료·워치독 이탈)와 없는 경로(R12 건너뛰기)가 **모두**
   *    여기로 들어온다. 분기를 두 군데 적으면 "영상 있는 스팟만 재출제된다" 로 갈린다.
   */
  const advanceAfterVerdict = useCallback(() => {
    setVerdictVideoUrl(null);
    if (lastCorrectRef.current) {
      startDoneScreen();
      return;
    }
    const answeredQids = answersRef.current.map((a) => a.qid);
    const next = pickNextQuestion(questions, answeredQids);
    if (!next) {
      startDoneScreen();
      return;
    }
    // 다음 문항으로 되돌린다 — 잠금을 함께 풀지 않으면 버튼이 죽은 채로 남는다.
    answeringRef.current = false;
    setLocked(false);
    setCurrent(next);
  }, [questions, startDoneScreen]);

  /** 정답/오답 영상의 **단일 출구** — 정상 종료·워치독 이탈 모두 여기로 나온다. */
  const handleVerdictFinished = useCallback(() => advanceAfterVerdict(), [advanceAfterVerdict]);

  const handleAnswer = useCallback(
    (chosen) => {
      // 이미 최종 제출됐거나 연출 중이면 무시(더블탭 차단 — ref 라 리렌더 대기 없음)
      if (!current || completedRef.current || answeringRef.current) return;
      // 🔴 여기서 completedRef 를 세우지 않는다 — 오답 재출제 경로가 잠겨 버린다.
      //    같은 문항 연타는 answeringRef 가 막고(같은 tick 에도 유효), 미션 종료 확정은
      //    startDoneScreen 한 곳에서만 한다.
      answeringRef.current = true;

      // 시도를 누적한다(D-B). 재출제는 늘 안 푼 문항이라 qid 중복이 생기지 않는다.
      answersRef.current = [...answersRef.current, { qid: current.qid, chosen }];
      lastCorrectRef.current = chosen === current.answer;

      setLocked(true); // 버튼 비활성 연출

      // 🔴 **정답 체크표식 폐지 — 답을 누르는 즉시 영상으로 간다** (2026-08-20 사용자 확정).
      //    옛 연출은 고른 값과 무관하게 *정답 쪽*에 검은 체크를 1.8초 깜박였다(R12,
      //    "오답을 지적하지 않는다"). 지금은 **선택×정오 4분기 영상**이 그 피드백을 그대로
      //    수행하므로 체크는 중복이고, 1.8초는 영상 시작을 늦출 뿐이다.
      //    지연 값만 0 으로 두지 않고 상태·요소·CSS 를 함께 걷었다 — 죽은 코드를 남기지 않는다.
      //
      // 🔴 정오 판정은 **어느 영상을 틀지 고르는 데만** 쓴다(표시용). 저장·정산의 권위는
      //    서버 재채점(D10)이고 result 는 그대로 chosen 만 담는다.
      const verdictUrl = resolveVerdictVideoUrl(config, chosen, lastCorrectRef.current);
      if (verdictUrl) {
        // 정답/오답 영상을 문제 화면 **위에 반투명 오버레이**로 재생(사용자 요청 R9).
        // 영상이 끝나거나 워치독이 걷어내면 handleVerdictFinished 가 완료 화면으로 넘긴다.
        setVerdictVideoUrl(verdictUrl);
      } else {
        // 영상이 없는 스팟(마이그170c 이전 레거시)은 **건너뛴다**(R12) — 빈 오버레이를 띄우지 않는다.
        // 건너뛰어도 분기는 같은 출구를 쓴다(정답=완료 / 오답=재출제).
        //
        // 🔴 **동기로 부르지 않는다.** `advanceAfterVerdict` 는 `answeringRef` 잠금을 푸는데,
        //    방금 이 핸들러가 건 잠금을 같은 동기 흐름에서 풀면 **이중탭 가드가 즉시 무력화**된다
        //    — 같은 이벤트 배치의 두 번째 탭이 다음 문항까지 삼킨다(2026-08-20 실측:
        //    답안 2개 기대에 4개가 쌓였다). 잠금을 건 호출이 끝난 뒤에 풀리도록 한 tick 미룬다.
        //    영상이 있는 경로는 오버레이가 화면과 버튼을 덮으므로 이 문제가 없다.
        verdictTimerRef.current.push(setTimeout(() => advanceAfterVerdict(), 0));
      }
    },
    [current, config, advanceAfterVerdict]
  );

  /**
   * USB 1버튼 키보드 2개(F9=O · F10=X) — 터치와 **같은 handleAnswer** 로 들어간다.
   *
   * 🔴 키 전용 분기를 따로 만들지 않는 이유: 잠금(answeringRef)·미션 종료(completedRef)·
   *    오답 재출제가 전부 handleAnswer 안에 있다. 분기를 나누면 그 가드가 한쪽에만 걸려
   *    "연출 중에 키로 답이 바뀐다" 거나 "답안이 두 번 쌓인다" 로 갈린다.
   *
   * document 에 붙이는 이유: 물리 버튼을 누를 때 화면에 포커스된 요소가 없다(아이가 터치로
   * 아무 데도 누르지 않은 상태). 요소 단위 onKeyDown 은 포커스가 없으면 오지 않는다.
   *
   * 브릿지앱은 이 키들을 소비하지 않고 WebView 로 넘기며(`KioskActivity.dispatchKeyEvent`),
   * `unicodeChar` 가 0 이라 QR 버퍼에도 쌓이지 않는다 — 앱 수정 없이 동작한다.
   */
  useEffect(() => {
    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;
      const chosen = resolveAnswerKey(event);
      if (!chosen) return;
      // 매핑된 키일 때만 기본 동작을 막는다(F10 메뉴바 등). 그 외 키는 건드리지 않는다.
      event.preventDefault();
      handleAnswer(chosen);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleAnswer]);

  if (total === 0) {
    return (
      <div className="ox-runner ox-runner-error" data-testid="ox-quiz-error">
        <span className="ox-error-icon">⚠️</span>
        <p className="ox-error-text">퀴즈 문항을 불러오지 못했어요.</p>
        {onCancel && (
          <button className="ox-btn ox-btn-cancel" onClick={onCancel}>
            돌아가기
          </button>
        )}
      </div>
    );
  }

  // ── 마무리 화면("미션 대성공!") — Figma 2295:75856, DONE_MS(5초) 후 QR 대기 화면 복귀 ──
  // 배경·게시판·동물 모두 기존 에셋 재사용(픽셀/md5 대조로 동일 확인).
  if (doneShown) {
    // 🔴 개정(Figma 3601:43294, 2026-08-13): 나무판 게시판 → 통짜 이미지 3장 구성.
    return <MissionDoneScreen />;
  }

  // 화면 구성은 Figma 3715:70497(1줄)·3715:70507(2줄) (2026-08-18 취득 — **개정본**).
  // 베이지 그라데이션 배경 + 말풍선 + 문제 + 문항 첨부 이미지 + 하단 O/X 두 개.
  // 옛 구조(나무판 배경·스팟명 제목·구분선·진행표시·440px 패널·좌하단 동물)는 전부 폐기했고,
  // 직전 개정(3510:48255, 파일에서 삭제됨)의 상단 고정 문구 자리는 말풍선이 대신한다.
  return (
    <div className="ox-quiz oxq-screen-beige" data-testid="ox-quiz-runner">
      {/* 말풍선(Figma Union) — 문제를 담는 그릇이라 문제보다 먼저 그린다.
          🔴 자산은 그림자까지 포함한 1860×454.702 라 몸통(1780×306)보다 크다 —
             좌표는 몸통이 아니라 **자산 상자** 기준이다(CSS 주석 참조). */}
      <div className="ox-quiz-bubble" data-testid="ox-quiz-bubble" aria-hidden="true" />

      <p
        className="ox-quiz-prompt"
        data-testid="ox-quiz-question"
        ref={promptRef}
        /* CSS 의 기본값(=상한)을 자동 맞춤 결과로 덮는다. 단위는 CSS 와 같은 --quiz-u 라
           해상도(1280/1920)가 달라져도 비율이 유지된다. */
        style={{ fontSize: `calc(${promptFont} * var(--quiz-u))` }}
      >
        {questionText}
      </p>

      {/* 문항에 첨부된 이미지 — 서버가 `image_url` 로 해석해 내려준다(계약 C2).
          🔴 없으면 **요소 자체를 그리지 않는다**(R8) — 확장 스키마 이전 스팟은 이미지가 없다. */}
      {questionImageUrl && (
        <img
          className="ox-quiz-image"
          src={questionImageUrl}
          alt=""
          data-testid="ox-quiz-image"
        />
      )}

      {/* O/X 선택 — 그림 자체가 버튼이라 라벨은 aria 로만 제공.
          히트박스(200)가 글리프(96)보다 넓다 — 어린이 터치 대상이라 CSS 에서 키웠다. */}
      <div className="ox-quiz-answers">
        <button
          type="button"
          className="ox-quiz-answer ox-quiz-answer-o"
          onClick={() => handleAnswer('O')}
          disabled={locked}
          aria-label="맞아요"
          data-testid="ox-quiz-choice-o"
        />
        <button
          type="button"
          className="ox-quiz-answer ox-quiz-answer-x"
          onClick={() => handleAnswer('X')}
          disabled={locked}
          aria-label="아니에요"
          data-testid="ox-quiz-choice-x"
        />

      </div>

      {/* 정답/오답 영상 — 문제 화면 **위에** 반투명 레이어로 얹는다(R9, Figma 3481:47885).
          아래 문제·이미지가 흐릿하게 비쳐야 하므로 이 요소는 형제가 아니라 같은 무대 안에 둔다. */}
      {verdictVideoUrl && (
        <OxVerdictOverlay
          videoUrl={verdictVideoUrl}
          onFinished={handleVerdictFinished}
          onAbort={onVideoAbort}
        />
      )}
    </div>
  );
}
